import environment from "flow/configuration/environment";
import config from "flow/config";
import localStorageChannel from "flow/session/localStorageChannel";
import UnsavedDataEnum from "flow/session/UnsavedDataEnum";
import MyFlow from "flow/MyFlow";
import SessionMonitor from "flow/session/SessionMonitor";
import InactivityMonitor from "flow/session/InactivityMonitor";
import activator from "flow/utils/activator";
import sessionStorage from "flow/wrappers/sessionStorage";
import localStorage from "flow/wrappers/localStorage";

const graceTimeUnsavedData = 1000;

export class Session {
    forcedLogout = false;
    logoutDebounce = false; // For IE11

    sessionMonitor: SessionMonitor;
    inactivityMonitor: InactivityMonitor;

    private myFlow: MyFlow;

    setup = (myFlow: MyFlow): void => {
        this.myFlow = myFlow;
        localStorageChannel.setup(myFlow);

        const inactivityTimeout = environment.get<number>(environment.keys.inactivityTimeout);

        this.sessionMonitor = new SessionMonitor(this);
        this.sessionMonitor.subscribeToLogouts();

        if (inactivityTimeout > 0) {
            this.inactivityMonitor = new InactivityMonitor(myFlow, this);
            this.inactivityMonitor.subscribeToActivity();
            this.inactivityMonitor.startInactivityTimer();
            this.inactivityMonitor.requestTabSynchronization();
        }
    };

    isWorkspaceDirty = (): boolean => {
        return !activator.canLeaveSweepWithoutUserFeedback();
    };

    stop = (): void => {
        this.sessionMonitor.unsubscribeToStorage();

        if (this.inactivityMonitor) {
            this.inactivityMonitor.stopInactivityTimer();
            this.inactivityMonitor.unsubscribeToActivity();
        }
    };

    logout = (): void => {
        if (this.isWorkspaceDirty()) {
            this.logoutDialog();
        } else {
            // Check if any tab has unsaved data
            localStorageChannel.clear(config.storageChannelTopics.unsavedData);
            localStorageChannel
                .query(
                    config.storageChannelTopics.unsavedData,
                    UnsavedDataEnum.Query,
                    graceTimeUnsavedData,
                    (message: string) => {
                        return message === UnsavedDataEnum.HasUnsavedData;
                    }
                )
                .then(() => {
                    this.logoutDialog();
                })
                .catch(() => {
                    // No response from other tabs (Ok)
                    // Will logout
                    this.forceLogout(true);
                });
        }
    };

    private logoutDialog = (): JQueryPromise<void> => {
        return this.myFlow
            .showConfirmDialog(
                this.myFlow.translate("FlowHtml.Translations.YouHaveUnsavedDataTitle"),
                this.myFlow.translate("FlowHtml.Translations.YouHaveUnsavedDataInTabText"),
                this.myFlow.translate("FlowHtml.Translations.Cancel"),
                this.myFlow.translate("FlowHtml.Translations.LogOut")
            )
            .then(result => {
                // To get logout as right side option
                if (result === false) {
                    this.forceLogout(true);
                }
            })
            .catch(() => {
                // Couldn't bring up dialog (unexpected)
                // Will logout
                this.forceLogout(true);
            });
    };

    forceLogout = (broadcast: boolean): void => {
        if (broadcast) {
            localStorageChannel.flash(config.storageChannelTopics.logout, config.forceLogoutMessage);
        }
        this.forcedLogout = true;
        this.internalLogout();
    };

    isForcedLogout = (): boolean => {
        return this.forcedLogout;
    };

    internalLogout = (): void => {
        if (this.logoutDebounce) {
            return;
        }

        this.logoutDebounce = true;
        this.stop();
        sessionStorage.removeItem(config.storageKeys.selectedProduct);
        // Show message again if new session.
        // Also avoid list of read messages building up between sessions.
        localStorage.removeItem(config.storageChannelTopics.systemInformationSeen);

        this.myFlow.publish(config.topics.loggingOut);

        window.location.href = environment.get(environment.keys.logoutUrl) as string;
    };

    registerActivity = (): void => {
        if (this.inactivityMonitor) {
            this.inactivityMonitor.registerActivityFromExternalSource();
        }
    };

    forceReloadWithBroadcast() {
        localStorageChannel.flash(config.storageChannelTopics.reload, "forced reload");
        this.forceReload();
    }

    forceReload() {
        this.stop();

        // Disable dirty checks
        environment.set(environment.keys.ignorePageUnloadDirtyCheck, true);
        // Force a page reload
        this.myFlow.refreshFlow(true, false);
    }
}

const session = new Session();
export default session;
